import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, X, BarChart2, Copy } from "lucide-react";

const EMPTY = { code: "", type: "discount_percent", value: 10, max_redemptions: null, plan_slugs: [], country_codes: [], expires_at: "", active: true };

export default function AdminCoupons() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    try { setRows((await api.get("/admin/coupons")).data.rows); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this coupon?")) return;
    try { await api.delete(`/admin/coupons/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openAnalytics = async (id) => {
    try { const { data } = await api.get(`/admin/coupons/${id}/analytics`); setAnalytics(data); } catch (e) {}
  };

  return (
    <AdminLayout
      subtitle="Module 10 · Commerce"
      title="Coupons"
      actions={<Button onClick={() => setShowNew(true)} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="coupons-new"><Plus className="w-4 h-4" />New coupon</Button>}
    >
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow text-zinc-500">Code</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Type</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Value</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Plans</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Redemptions</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Expires</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Status</th>
                <th className="px-4 py-3 eyebrow text-zinc-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`coupon-row-${c.code}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-plex font-bold text-white">{c.code}</span>
                      <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Copied"); }} className="text-zinc-500 hover:text-white"><Copy className="w-3 h-3" /></button>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge className="bg-white/10 border-white/10 rounded-sm">{c.type}</Badge></td>
                  <td className="px-4 py-3 font-display font-bold text-white">{c.type === "discount_percent" ? `${c.value}%` : `$${c.value}`}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{(c.plan_slugs || []).join(", ") || "all"}</td>
                  <td className="px-4 py-3 font-mono-plex text-zinc-300">{c.redemptions || 0}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                  <td className="px-4 py-3 text-xs font-mono-plex text-zinc-500">{c.expires_at?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3">{c.active ? <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 rounded-sm">Active</Badge> : <Badge className="bg-white/5 text-zinc-500 border-white/10 rounded-sm">Disabled</Badge>}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => openAnalytics(c.id)} className="p-1.5 rounded-sm border border-white/10 text-zinc-300 hover:bg-white/10" data-testid={`coupon-analytics-${c.code}`}><BarChart2 className="w-3 h-3" /></button>
                      <button onClick={() => setEditing(c)} className="p-1.5 rounded-sm border border-white/10 text-zinc-300 hover:bg-white/10" data-testid={`coupon-edit-${c.code}`}>Edit</button>
                      <button onClick={() => del(c.id)} className="p-1.5 rounded-sm border border-ec-error/30 text-ec-error hover:bg-ec-error/10" data-testid={`coupon-delete-${c.code}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-zinc-500" data-testid="coupons-empty">No coupons yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {(showNew || editing) && (
        <CouponModal initial={editing || EMPTY} isNew={!editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={() => { setShowNew(false); setEditing(null); load(); }} />
      )}
      {analytics && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setAnalytics(null)} data-testid="coupon-analytics-modal">
          <div className="bg-ec-ink border border-white/10 rounded-sm max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="eyebrow text-ec-emerald">Analytics</div>
                <h3 className="font-display text-2xl font-bold text-white">{analytics.coupon.code}</h3>
              </div>
              <button onClick={() => setAnalytics(null)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10 mb-4">
              <div className="bg-ec-ink p-4">
                <div className="eyebrow text-zinc-500">Redemptions</div>
                <div className="font-display text-3xl font-black text-white">{analytics.redemptions.length}</div>
              </div>
              <div className="bg-ec-ink p-4">
                <div className="eyebrow text-zinc-500">Revenue impact</div>
                <div className="font-display text-3xl font-black text-white">${analytics.revenue_impact.toFixed(2)}</div>
              </div>
            </div>
            <ul className="max-h-72 overflow-y-auto text-xs space-y-1 font-mono-plex">
              {analytics.redemptions.length === 0
                ? <li className="text-zinc-500 text-center py-6">No redemptions yet.</li>
                : analytics.redemptions.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 py-1 border-b border-white/5">
                    <span className="text-zinc-500 w-32">{r.created_at?.slice(0, 16)}</span>
                    <span className="text-zinc-300 flex-1">{r.user_id}</span>
                    <span className="text-ec-emerald">−${r.discount_amount?.toFixed(2)}</span>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function CouponModal({ initial, isNew, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, plan_slugs: (initial.plan_slugs || []).join(", "), country_codes: (initial.country_codes || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        code: (f.code || "").toUpperCase(),
        value: parseFloat(f.value) || 0,
        max_redemptions: f.max_redemptions ? parseInt(f.max_redemptions) : null,
        plan_slugs: (f.plan_slugs || "").split(",").map((s) => s.trim()).filter(Boolean),
        country_codes: (f.country_codes || "").split(",").map((s) => s.trim()).filter(Boolean),
        expires_at: f.expires_at || null,
      };
      if (isNew) {
        await api.post("/admin/coupons", payload);
        toast.success("Coupon created");
      } else {
        await api.patch(`/admin/coupons/${initial.id}`, payload);
        toast.success("Coupon updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="coupon-modal">
      <div className="bg-ec-ink border border-white/10 rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="font-display text-xl font-bold text-white">{isNew ? "New coupon" : "Edit coupon"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <ModalField label="Code *"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} className="rounded-sm bg-white/5 border-white/10 text-white font-mono-plex" data-testid="coupon-input-code" /></ModalField>
          <ModalField label="Type">
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="rounded-sm h-10 px-3 bg-white/5 border border-white/10 text-white text-sm w-full" data-testid="coupon-input-type">
              <option value="discount_percent" className="bg-ec-ink">discount_percent</option>
              <option value="discount_fixed" className="bg-ec-ink">discount_fixed</option>
              <option value="free_trial_days" className="bg-ec-ink">free_trial_days</option>
              <option value="lifetime" className="bg-ec-ink">lifetime</option>
            </select>
          </ModalField>
          <ModalField label="Value"><Input type="number" step="0.01" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="coupon-input-value" /></ModalField>
          <ModalField label="Max redemptions (optional)"><Input type="number" value={f.max_redemptions || ""} onChange={(e) => setF({ ...f, max_redemptions: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="coupon-input-max" /></ModalField>
          <ModalField label="Plan slugs (comma)"><Input value={f.plan_slugs} onChange={(e) => setF({ ...f, plan_slugs: e.target.value })} placeholder="professional_pro, employer_growth" className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="coupon-input-plans" /></ModalField>
          <ModalField label="Country codes (comma)"><Input value={f.country_codes} onChange={(e) => setF({ ...f, country_codes: e.target.value })} placeholder="IN, US" className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="coupon-input-countries" /></ModalField>
          <ModalField label="Expires at (ISO)"><Input type="datetime-local" value={f.expires_at || ""} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="coupon-input-expires" /></ModalField>
          <ModalField label="Active">
            <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="coupon-input-active" /><span className="text-sm text-zinc-300">Coupon is active</span></label>
          </ModalField>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <Button variant="outline" className="rounded-sm border-white/10 text-white hover:bg-white/10" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink" data-testid="coupon-save">{saving ? "Saving…" : "Save coupon"}</Button>
        </div>
      </div>
    </div>
  );
}
function ModalField({ label, children }) {
  return (<div><Label className="eyebrow text-zinc-500">{label}</Label><div className="mt-2">{children}</div></div>);
}
